/**
 * DealsPanel — Reusable panel that lists Client ↔ Forklift relationships ("deals").
 *
 * Two modes (controlled by `mode` prop):
 *   - mode="client"    → shows all deals belonging to a single client.
 *                         Each row shows the machine + role + status + date + notes.
 *                         Has an "Add" form to link this client to an inventory machine.
 *   - mode="forklift"  → shows all deals attached to a single forklift.
 *                         Each row shows the client + role + status + date + notes.
 *                         Has an "Add" form to link an existing CRM client to this machine.
 *
 * Talks to the new backend endpoints `/api/crm/deals`.
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Truck, User, Handshake } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ROLE_LABELS = {
  seller: "Vendeur",
  buyer: "Acheteur",
  interested: "Intéressé",
};
export const ROLE_COLORS = {
  seller: "bg-purple-100 text-purple-800",
  buyer: "bg-green-100 text-green-800",
  interested: "bg-blue-100 text-blue-800",
};

export const DEAL_STATUS_LABELS = {
  new: "Nouveau",
  contacted: "Contacté",
  negotiating: "Négociation",
  closed_won: "Gagné",
  closed_lost: "Perdu",
};
export const DEAL_STATUS_COLORS = {
  new: "bg-blue-100 text-blue-800",
  contacted: "bg-yellow-100 text-yellow-800",
  negotiating: "bg-orange-100 text-orange-800",
  closed_won: "bg-green-100 text-green-800",
  closed_lost: "bg-gray-100 text-gray-500",
};

export default function DealsPanel({ mode, clientId, forkliftId }) {
  const isClientMode = mode === "client";
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [options, setOptions] = useState([]); // forklifts (client mode) or clients (forklift mode)
  const [form, setForm] = useState({ pairId: "", role: "interested", status: "new", notes: "" });
  const [saving, setSaving] = useState(false);

  const fetchDeals = useCallback(async () => {
    setLoading(true);
    try {
      const params = isClientMode ? { client_id: clientId } : { forklift_id: forkliftId };
      const { data } = await axios.get(`${API}/crm/deals`, { params, withCredentials: true });
      setDeals(data);
    } catch (e) {
      console.error("Failed to load deals", e);
    } finally {
      setLoading(false);
    }
  }, [isClientMode, clientId, forkliftId]);

  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);

  const loadOptions = async () => {
    try {
      if (isClientMode) {
        const { data } = await axios.get(`${API}/forklifts?status=available`);
        setOptions(data);
      } else {
        const { data } = await axios.get(`${API}/crm/clients`, { withCredentials: true });
        setOptions(data);
      }
    } catch (e) {
      console.error("Failed to load options", e);
    }
  };

  const openForm = () => {
    setShowForm(true);
    setForm({ pairId: "", role: "interested", status: "new", notes: "" });
    loadOptions();
  };

  const createDeal = async () => {
    if (!form.pairId) {
      toast.error(isClientMode ? "Sélectionnez une machine" : "Sélectionnez un client");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        client_id: isClientMode ? clientId : form.pairId,
        forklift_id: isClientMode ? form.pairId : forkliftId,
        role: form.role,
        status: form.status,
        notes: form.notes || "",
      };
      await axios.post(`${API}/crm/deals`, payload, { withCredentials: true });
      toast.success("Relation créée");
      setShowForm(false);
      fetchDeals();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setSaving(false);
    }
  };

  const updateDeal = async (dealId, patch) => {
    try {
      await axios.put(`${API}/crm/deals/${dealId}`, patch, { withCredentials: true });
      fetchDeals();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const deleteDeal = async (dealId) => {
    if (!window.confirm("Supprimer cette relation ?")) return;
    try {
      await axios.delete(`${API}/crm/deals/${dealId}`, { withCredentials: true });
      toast.success("Supprimée");
      fetchDeals();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <div data-testid="deals-panel" className="bg-white border border-gray-200 rounded-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-sm font-bold text-gray-900 flex items-center gap-2">
          <Handshake className="w-4 h-4 text-yellow-600" />
          {isClientMode ? "Machines reliées" : "Clients intéressés"}
          <span className="text-gray-400 font-normal">({deals.length})</span>
        </h3>
        <button
          data-testid="deals-add-btn"
          onClick={openForm}
          className="inline-flex items-center gap-1.5 bg-yellow-400 text-gray-900 font-bold hover:bg-yellow-500 px-3 py-1.5 rounded-sm text-xs transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Ajouter
        </button>
      </div>

      {/* Add form */}
      {showForm && (
        <div data-testid="deals-add-form" className="border border-yellow-200 bg-yellow-50/50 rounded-sm p-4 mb-4 space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-700 mb-1 block">
              {isClientMode ? "Machine" : "Client"} *
            </label>
            <Select value={form.pairId} onValueChange={(v) => setForm({ ...form, pairId: v })}>
              <SelectTrigger data-testid="deals-add-pair" className="bg-white">
                <SelectValue placeholder={isClientMode ? "Choisir une machine…" : "Choisir un client…"} />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                {options.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {isClientMode
                      ? `${o.brand || ""} ${o.model || ""}${o.year ? ` (${o.year})` : ""}${o.price ? ` — $${o.price.toLocaleString()}` : ""}`
                      : `${o.name}${o.company ? ` (${o.company})` : ""}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-700 mb-1 block">Rôle</label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="deals-add-role" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700 mb-1 block">Statut</label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="deals-add-status" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(DEAL_STATUS_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700 mb-1 block">Notes</label>
            <Textarea
              data-testid="deals-add-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Détails de la relation (optionnel)"
              className="bg-white min-h-[60px]"
            />
          </div>
          <div className="flex gap-2">
            <button
              data-testid="deals-add-save"
              onClick={createDeal}
              disabled={saving}
              className="inline-flex items-center gap-1.5 bg-yellow-400 text-gray-900 font-bold hover:bg-yellow-500 px-4 py-1.5 rounded-sm text-xs disabled:opacity-50"
            >
              {saving ? "..." : "Enregistrer"}
            </button>
            <button
              data-testid="deals-add-cancel"
              onClick={() => setShowForm(false)}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <p className="text-sm text-gray-400 py-3">Chargement…</p>
      ) : deals.length === 0 ? (
        <p data-testid="deals-empty" className="text-sm text-gray-400 italic py-3">
          Aucune relation enregistrée. Cliquez sur « Ajouter » pour en créer une.
        </p>
      ) : (
        <div className="space-y-2" data-testid="deals-list">
          {deals.map((d) => {
            const pair = isClientMode ? d.forklift : d.client;
            if (!pair) return null;
            const pairLabel = isClientMode
              ? `${pair.brand || ""} ${pair.model || ""}${pair.year ? ` (${pair.year})` : ""}`
              : pair.name;
            const pairSub = isClientMode
              ? `${pair.condition === "new" ? "Neuf" : "Usagé"} · ${pair.capacity_lbs || "?"} lbs · ${pair.engine_type || ""}${pair.price ? ` · $${pair.price.toLocaleString()}` : ""}`
              : `${pair.company || ""}${pair.phone ? ` · ${pair.phone}` : ""}${pair.email ? ` · ${pair.email}` : ""}`;
            const pairHref = isClientMode
              ? `/admin/inventory?focus=${pair.id}`
              : `/admin/crm/clients/${pair.id}`;
            const Icon = isClientMode ? Truck : User;
            return (
              <div
                key={d.id}
                data-testid={`deal-row-${d.id}`}
                className="border border-gray-200 rounded-sm p-3 bg-gray-50/40 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="bg-white border border-gray-200 rounded-sm p-2 flex-shrink-0">
                      <Icon className="w-4 h-4 text-gray-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <Link to={pairHref} className="font-semibold text-sm text-gray-900 hover:text-yellow-700 truncate block">
                        {pairLabel}
                      </Link>
                      <p className="text-xs text-gray-500 truncate">{pairSub}</p>
                      {d.notes && <p className="text-xs text-gray-700 mt-1 italic">{d.notes}</p>}
                    </div>
                  </div>
                  <button
                    data-testid={`deal-delete-${d.id}`}
                    onClick={() => deleteDeal(d.id)}
                    className="text-gray-300 hover:text-red-500 transition-colors flex-shrink-0"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <Select value={d.role} onValueChange={(v) => updateDeal(d.id, { role: v })}>
                    <SelectTrigger data-testid={`deal-role-${d.id}`} className={`h-7 w-auto text-xs border-none px-2.5 ${ROLE_COLORS[d.role] || "bg-gray-100 text-gray-700"}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ROLE_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={d.status} onValueChange={(v) => updateDeal(d.id, { status: v })}>
                    <SelectTrigger data-testid={`deal-status-${d.id}`} className={`h-7 w-auto text-xs border-none px-2.5 ${DEAL_STATUS_COLORS[d.status] || "bg-gray-100 text-gray-700"}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(DEAL_STATUS_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Badge variant="outline" className="text-xs font-normal text-gray-500">
                    {d.date}
                  </Badge>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
